/**
 * The Activity feed (`/{ws}/activity`, GET /v1/w/:ws/activity?all=1): who sees which kind of item, how one renders,
 * the filters (All / Mentions / Threads / Reactions) and the unread count behind the sidebar badge.
 * The server records, per person: @-mentions (by name, or @here/@channel/@everyone) in channels, replies in threads
 * they follow (started or replied in; one item per thread), reactions to their messages (one item per message and
 * emoji) and being added to a channel; plus `member_joined` (someone joined through an allowed email domain,
 * logic/emailDomains) for owners and admins. DMs stay out (they have their own unread state).
 */
import type { ActivityItem, Channel, Member, Role } from "@bundle/contracts";
import { type MessageDescriptor, msg } from "../i18n.ts";
import { type Route, formatUrl } from "./shell.ts";

export type ActivityKind = ActivityItem["kind"];

/** Most items the server keeps listing (newest first). */
export const ACTIVITY_LIMIT = 100;

/** Every kind, in the order the server checks them. */
export const ACTIVITY_KINDS: readonly ActivityKind[] = ["mention", "thread_reply", "reaction", "channel_added", "reminder", "event_reminder", "member_joined"];

/**
 * Whether a member with this role sees this kind of item. Joins: owners and admins, who manage the people list.
 * Everything else is addressed to one person, who always sees their own.
 */
export function canSeeActivity(kind: ActivityKind, role: Role): boolean {
  switch (kind) {
    case "member_joined":
      return role === "owner" || role === "admin";
    case "mention":
    case "thread_reply":
    case "reaction":
    case "channel_added":
    case "reminder":
    case "event_reminder":
      return true;
  }
}

// ---- Filters ----

export const ACTIVITY_FILTERS = ["all", "mentions", "threads", "reactions"] as const;
export type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];

/** Whether an item of this kind shows under a filter (All shows everything). */
export function matchesFilter(kind: ActivityKind, filter: ActivityFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "mentions":
      return kind === "mention";
    case "threads":
      return kind === "thread_reply";
    case "reactions":
      return kind === "reaction";
  }
}

// ---- Unread ----

/** Unread until opened or "Mark all as read" (absent = read). */
export function isUnread(item: ActivityItem): boolean {
  return item.unread === true;
}

/** The sidebar Activity badge: unread items (duplicates counted once). */
export function unreadCount(items: readonly ActivityItem[]): number {
  const seen = new Set<string>();
  let n = 0;
  for (const it of items) {
    if (seen.has(it.id)) continue;
    seen.add(it.id);
    if (isUnread(it)) n++;
  }
  return n;
}

/** Marks these items read (`ids` absent = all of them), as the server will; others are returned unchanged. */
export function markRead(items: readonly ActivityItem[], ids?: readonly string[]): ActivityItem[] {
  const only = ids === undefined ? undefined : new Set(ids);
  return items.map((it) => (isUnread(it) && (only === undefined || only.has(it.id)) ? { ...it, unread: false } : it));
}

// ---- Rows ----

/** The person's name as the feed shows it (full name, else display name). */
function personName(member: Member | undefined): string | undefined {
  if (member === undefined) return undefined;
  return member.fullName || member.displayName || undefined;
}

function isDirect(channel: Channel | undefined): boolean {
  return channel?.kind === "dm" || channel?.kind === "group_dm";
}

export type ActivityRow = {
  id: string;
  kind: ActivityKind;
  /** Whose avatar to show (who mentioned, replied, reacted, added me, or joined; "" for a reminder: a bell). */
  userId: string;
  /** "Bob Chen mentioned you in #design". */
  text: MessageDescriptor;
  /** The message as one line (mentions, replies: theirs; reactions: mine). Absent for joins and channel adds. */
  preview?: string;
  /** Reactions: the emoji, shown large next to the avatar. */
  emoji?: string;
  ts: number;
  unread: boolean;
  /** In-app route a click opens: the thread for a reply in one, else the conversation (joins: a DM with them). */
  route: Route;
  /** The message to scroll to and highlight once the route is open. */
  focusMessageId?: string;
};

/** "Ana invited you", "Ana changed the time", "Ana cancelled this event"; a plain reminder says it's coming up. */
function eventText(item: Extract<ActivityItem, { kind: "event_reminder" }>, name: string | undefined): MessageDescriptor {
  switch (item.change) {
    case undefined:
      return msg("activity.eventReminder");
    case "invited":
      return name === undefined ? msg("activity.eventInvited.someone") : msg("activity.eventInvited", { name });
    case "changed":
      return name === undefined ? msg("activity.eventChanged.someone") : msg("activity.eventChanged", { name });
    case "cancelled":
      return name === undefined ? msg("activity.eventCancelled.someone") : msg("activity.eventCancelled", { name });
  }
}

/** The conversation, or the thread for a message in one. */
function messageRoute(ws: string, channelId: string, threadRootId: string | undefined): Route {
  return threadRootId === undefined ? { type: "channel", ws, channelId } : { type: "thread", ws, channelId, rootId: threadRootId };
}

/** One item as a feed row. Someone not in my directory (or a reply/reaction with no one listed) reads as "Someone". */
export function activityRow(item: ActivityItem, members: Record<string, Member>, channels: Record<string, Channel>, ws: string): ActivityRow {
  const unread = isUnread(item);
  switch (item.kind) {
    case "member_joined": {
      const name = personName(members[item.userId]);
      const text =
        name === undefined ? msg("activity.memberJoined.someone", { domain: item.domain }) : msg("activity.memberJoined", { name, domain: item.domain });
      return { id: item.id, kind: item.kind, userId: item.userId, text, ts: item.ts, unread, route: { type: "dm", ws, userId: item.userId } };
    }
    case "mention": {
      const name = personName(members[item.userId]) ?? "";
      const channel = channels[item.channelId]?.name ?? "";
      // A user group reads like a broadcast: "Ana mentioned @design in #general".
      const range = item.broadcast ?? item.groupHandle;
      const text =
        range === undefined
          ? msg(name === "" ? "activity.mention.someone" : "activity.mention", { name, channel })
          : msg(name === "" ? "activity.broadcast.someone" : "activity.broadcast", { name, channel, range });
      return {
        id: item.id,
        kind: item.kind,
        userId: item.userId,
        text,
        preview: item.preview,
        ts: item.ts,
        unread,
        route: messageRoute(ws, item.channelId, item.threadRootId),
        focusMessageId: item.messageId,
      };
    }
    case "thread_reply": {
      const first = item.userIds[0] ?? "";
      const name = personName(members[first]) ?? "";
      const others = Math.max(0, item.userIds.length - 1);
      const ch = channels[item.channelId];
      const direct = isDirect(ch);
      const text =
        others > 0
          ? msg(direct ? "activity.threadReply.others.dm" : "activity.threadReply.others", { name, count: others, channel: ch?.name ?? "" })
          : msg(direct ? "activity.threadReply.dm" : "activity.threadReply", { name, channel: ch?.name ?? "" });
      return {
        id: item.id,
        kind: item.kind,
        userId: first,
        text,
        preview: item.preview,
        ts: item.ts,
        unread,
        route: messageRoute(ws, item.channelId, item.threadRootId),
        focusMessageId: item.messageId,
      };
    }
    case "reaction": {
      const first = item.userIds[0] ?? "";
      const name = personName(members[first]) ?? "";
      const others = Math.max(0, item.userIds.length - 1);
      const text = others > 0 ? msg("activity.reaction.others", { name, count: others, emoji: item.emoji }) : msg("activity.reaction", { name, emoji: item.emoji });
      return {
        id: item.id,
        kind: item.kind,
        userId: first,
        text,
        preview: item.preview,
        emoji: item.emoji,
        ts: item.ts,
        unread,
        route: messageRoute(ws, item.channelId, item.threadRootId),
        focusMessageId: item.messageId,
      };
    }
    case "channel_added": {
      const name = personName(members[item.userId]) ?? "";
      const channel = channels[item.channelId]?.name ?? "";
      return {
        id: item.id,
        kind: item.kind,
        userId: item.userId,
        text: msg(name === "" ? "activity.channelAdded.someone" : "activity.channelAdded", { name, channel }),
        ts: item.ts,
        unread,
        route: { type: "channel", ws, channelId: item.channelId },
      };
    }
    case "reminder": {
      // A saved message's reminder opens the message in context; a text reminder opens Later.
      const row: ActivityRow = { id: item.id, kind: item.kind, userId: "", text: msg("activity.reminder"), preview: item.preview, ts: item.ts, unread, route: { type: "later", ws } };
      if (item.channelId !== undefined) {
        row.route = messageRoute(ws, item.channelId, item.threadRootId);
        if (item.messageId !== undefined) row.focusMessageId = item.messageId;
      }
      return row;
    }
    case "event_reminder":
      // An upcoming event of mine, or the organizer's news about one: opens it in Calendar, on its day.
      return {
        id: item.id,
        kind: item.kind,
        userId: item.by ?? "",
        text: eventText(item, personName(item.by === undefined ? undefined : members[item.by])),
        preview: item.title,
        ts: item.ts,
        unread,
        route: { type: "calendar", ws, view: "day", at: item.start, event: item.eventId },
      };
  }
}

/** The feed under a filter: newest first (ties by id), duplicates dropped. */
export function activityRows(
  items: readonly ActivityItem[],
  members: Record<string, Member>,
  channels: Record<string, Channel>,
  ws: string,
  filter: ActivityFilter = "all",
): ActivityRow[] {
  const seen = new Set<string>();
  const unique: ActivityItem[] = [];
  for (const it of items) {
    if (seen.has(it.id)) continue;
    seen.add(it.id);
    if (matchesFilter(it.kind, filter)) unique.push(it);
  }
  unique.sort((a, b) => b.ts - a.ts || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return unique.map((it) => activityRow(it, members, channels, ws));
}

/** Where a click on an Activity row goes. */
export function activityUrl(row: ActivityRow): string {
  return formatUrl(row.route);
}

/** The part of a realtime `notification` (contracts NotificationEvent) the Activity badge needs. */
export type ActivityHint = {
  about?: "message" | "thread_reply" | "huddle" | "reminder";
  kind: "dm" | "mention" | "broadcast";
  userId: string;
  channelId: string;
  messageId: string;
  threadRootId?: string;
  body: string;
  ts: number;
};

/** Id of an item made here from a notification, until the next fetch brings the server's. */
export const LIVE_ITEM_PREFIX = "live:";

/**
 * Applies a realtime notification to my Activity items at once, so the badge moves without waiting for a fetch (the
 * server's list replaces it on the next refresh): a reply in a thread I follow bumps that thread's item (its count
 * starts over once read) or adds one; an @-mention in a channel adds a mention item. DMs (not in Activity), huddle
 * rings, my own messages and items already there change nothing. Newest first, at most ACTIVITY_LIMIT.
 */
export function applyNotification(items: readonly ActivityItem[], hint: ActivityHint, meId: string): ActivityItem[] {
  if (hint.userId === meId || hint.about === "huddle" || hint.about === "reminder") return [...items];
  let next: ActivityItem | undefined;
  let rest: ActivityItem[] = [...items];
  if (hint.about === "thread_reply" && hint.threadRootId !== undefined) {
    const rootId = hint.threadRootId;
    const old = items.find((i) => i.kind === "thread_reply" && i.threadRootId === rootId);
    if (old !== undefined && old.kind === "thread_reply") {
      if (old.messageId === hint.messageId) return [...items];
      const before = old.unread === true ? old.userIds : [];
      next = { ...old, userIds: [hint.userId, ...before.filter((u) => u !== hint.userId)].slice(0, 20), count: old.unread === true ? old.count + 1 : 1, messageId: hint.messageId, preview: hint.body, ts: hint.ts, unread: true };
      rest = items.filter((i) => i !== old);
    } else {
      next = { id: `${LIVE_ITEM_PREFIX}${hint.messageId}`, kind: "thread_reply", userIds: [hint.userId], count: 1, channelId: hint.channelId, threadRootId: rootId, messageId: hint.messageId, preview: hint.body, ts: hint.ts, unread: true };
    }
  } else if (hint.kind !== "dm" && (hint.about === undefined || hint.about === "message")) {
    if (items.some((i) => i.kind === "mention" && i.messageId === hint.messageId)) return [...items];
    const item: ActivityItem = { id: `${LIVE_ITEM_PREFIX}${hint.messageId}`, kind: "mention", userId: hint.userId, channelId: hint.channelId, messageId: hint.messageId, preview: hint.body, ts: hint.ts, unread: true };
    if (hint.threadRootId !== undefined) item.threadRootId = hint.threadRootId;
    next = item;
  }
  if (next === undefined) return [...items];
  return [next, ...rest].sort((a, b) => b.ts - a.ts).slice(0, ACTIVITY_LIMIT);
}
